import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Dashboard from '../dashboard';
import Files from './files';
import Actions from './actions';
import { importUrl as importFromUrl } from '../import/importSlice';
import { useQuery, useUpdateQuery } from '../../hooks/urlHooks';
import { URLParamsType } from '../../types';
import useLimits from '../plan/hooks/limits';
import useUserTags from '../../hooks/useTags';
import TeamDeletionCover from '../team/TeamDeletingCover';
import { RootState } from '../../reducers';
import MultiAskAI from './files/MultiAskAI';
import { pushModal } from '../globalModals/globalModalsSlice';
import UploadDropZone from './UploadDropZone';
import { USER_PACKAGE } from '../plan/userPackage';
import SocialMediaImportDialog from '../../components/SocialMediaImportDialog';
import ImportLoadingModal from '../../components/ImportLoadingModal';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import { useBooleanFeature } from '../../hooks/useFeatures';
import { SelectModeProvider } from './context/SelectModeContext';

const ONBOARDING_STEP_TAG_VALUE = '2024-Dec';

export const Library = () => {
  const { teamId, folderId } = useParams<URLParamsType>();
  const { importUrl: importUrlFromQuery, intent } = useQuery();
  const updateQuery = useUpdateQuery();
  const dispatch = useDispatch();
  const guardLimit = useLimits();
  const { getTag } = useUserTags();
  const { getFeature } = useBooleanFeature();
  const { team } = useSelector((state: RootState) => state);
  const teamDeleteRequested = !!team.selectedTeam?.deleteRequestedAt;
  const { files } = useSelector((state: RootState) => state.library);
  const { subscription } = useSelector((state: RootState) => state.plan);
  const isFreeUser = subscription?.package === USER_PACKAGE.FREE_LOGGEDIN;
  const selectedFolder = useSelector(
    (state: RootState) => state.library.selectedFolder
  );
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [showImportLoading, setShowImportLoading] = useState(false);
  const onboardingModalEnabled = getFeature('feature_enable_onboarding');

  // Helper function to check if a URL is Instagram
  const isInstagramUrl = (url: string): boolean => {
    const lowercaseUrl = url.toLowerCase();
    return (
      lowercaseUrl.includes('instagram.com/p/') ||
      lowercaseUrl.includes('instagram.com/reel/') ||
      lowercaseUrl.includes('instagram.com/tv/')
    );
  };

  // Helper function to check if a URL is from YouTube (premium import)
  const isPremiumImportUrl = (url: string): boolean => {
    const lowercaseUrl = url.toLowerCase();
    return (
      // YouTube URLs only
      lowercaseUrl.includes('youtube') || lowercaseUrl.includes('youtu.be')
    );
  };

  // Update page title
  useEffect(() => {
    document.title = `${selectedFolder?.name || 'Library'} - ScreenApp`;
    return () => {
      document.title = 'ScreenApp';
    };
  }, [selectedFolder?.name]);

  // Check for archived recordings when selected team changes
  useEffect(() => {
    if (team.selectedTeam?.archivedAt) {
      dispatch(
        setAlertMessage({
          type: 'info',
          message:
            'Your archived recordings will be unarchived and once complete we will let you know through an email.',
        })
      );
    }
  }, [team.selectedTeam]);

  // Handle actions based on query params
  useEffect(() => {
    const handleImport = async () => {
      if (importUrlFromQuery && teamId && folderId) {
        setShowImportLoading(true);
        try {
          // Check if it's an Instagram URL - open ImportModal with auto-filled URL
          if (isInstagramUrl(importUrlFromQuery)) {
            dispatch(
              pushModal({
                type: 'import-urls-modal',
                url: importUrlFromQuery,
              })
            );
            setShowImportLoading(false);
            updateQuery({ importUrl: undefined, intent: undefined });
            return;
          }

          // Only show upgrade dialog for free users attempting to import premium content (YouTube)
          if (isFreeUser && isPremiumImportUrl(importUrlFromQuery)) {
            // Show YouTube upgrade dialog
            setMediaUrl(importUrlFromQuery);
            setImportDialogOpen(true);
          } else {
            // Import from non-premium URL or paid user URL
            await dispatch(
              importFromUrl({
                teamId,
                folderId,
                url: importUrlFromQuery,
                intent,
              })
            );
          }
        } catch (error) {
          // Error is already handled in the thunk
          console.error('Import failed:', error);
        } finally {
          setShowImportLoading(false);
          // Clear the import URL from query params immediately after starting the import
          updateQuery({ importUrl: undefined, intent: undefined });
        }
      }
      if (files.data.length > 1) {
        guardLimit('login', () => {});
      }
    };

    handleImport();
  }, [importUrlFromQuery]);

  useEffect(() => {
    if (
      onboardingModalEnabled &&
      files.status === 'succeeded' &&
      getTag('onboardingStep') !== ONBOARDING_STEP_TAG_VALUE
    ) {
      dispatch(pushModal({ type: 'onboarding-modal' }));
    }
  }, [files.status]);

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setMediaUrl('');
  };

  if (teamDeleteRequested) {
    return (
      <Dashboard>
        <TeamDeletionCover />
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <SelectModeProvider>
        <UploadDropZone>
          <Box sx={{ height: 150, mt: 0.5 }}>
            <Actions />
            {templateModalOpen && (
              <MultiAskAI
                open={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
              />
            )}
          </Box>
          <Box sx={{ p: 2 }}>
            <Files />
          </Box>
        </UploadDropZone>

        {/* YouTube Import Dialog */}
        <SocialMediaImportDialog
          open={importDialogOpen}
          onClose={handleCloseImportDialog}
          url={mediaUrl}
        />

        {/* Import Loading Modal */}
        <ImportLoadingModal open={showImportLoading} />
      </SelectModeProvider>
    </Dashboard>
  );
};

export default Library;
